import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {of} from 'rxjs';
import {catchError, map, mergeMap, tap, withLatestFrom} from 'rxjs/operators';
import {clareProductToProduct, Product} from '../../../models/clare';
import {ProductApiService} from '../../../services/product/product-api.service';
import {MISSING_CATALOG_ERROR_CODE} from '../../catalog-list/store/catalog.constants';
import {emptyPagedListResponse, isActiveListSearch, isNotFoundError} from '../../../utils/list-page-utils';
import {buildListApiQueryParams, buildSelectionListApiQueryParams} from './product-list-query-params';
import {ProductListActions} from './product-list.actions';
import {selectSelectedOrganizationId} from '../../../state/router/router.selectors';

@Injectable()
export class ProductListEffects {
  toggleProductActiveStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductListActions.toggleProductActiveStatus),
      mergeMap(({productId, active}) =>
        this.productApi.update({id: Number(productId), active}).pipe(
          map(clareProductToProduct),
          map(product => ProductListActions.toggleProductActiveStatusSuccess({product})),
          catchError(() =>
            of(
              ProductListActions.toggleProductActiveStatusFailure({
                error: {message: 'An error occurred. Please refresh and try again.'},
              })
            )
          )
        )
      )
    )
  );

  loadProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductListActions.loadProducts),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      mergeMap(([{queryParams}, organizationId]) => {
        const cleanedParams = buildListApiQueryParams(queryParams);
        const catalogId = cleanedParams.catalogId;

        return this.productApi.getProducts(catalogId, cleanedParams, organizationId).pipe(
          map(products => ProductListActions.loadProductsSuccess({products})),
          catchError(error => {
            if (isActiveListSearch(cleanedParams) && isNotFoundError(error)) {
              return of(
                ProductListActions.loadProductsSuccess({
                  products: emptyPagedListResponse<Product>(cleanedParams, 'Items::Product'),
                })
              );
            }

            return of(
              ProductListActions.loadProductsFailure({
                error: {message: 'An error occurred, please refresh and try again.', code: 'PM-404'},
              })
            );
          })
        );
      })
    )
  );

  loadProductsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductListActions.loadProductsFailure),
        tap(({error}) => {
          this.router.navigateByUrl('/error', {state: {error}});
        })
      ),
    {dispatch: false}
  );

  /**
   * Loads a paged product list for the choose-bundle-products modal. Plain API
   * fetch; unlike the products tab this does not default the list sort.
   */
  loadProductsForSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductListActions.loadProductsForSelection),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      mergeMap(([{queryParams}, orgId]) => {
        return this.productApi.getProductsByOrganization(orgId!, buildSelectionListApiQueryParams(queryParams)).pipe(
          map(products => ProductListActions.loadProductsForSelectionSuccess({products})),
          catchError(error => {
            if (isActiveListSearch(queryParams) && isNotFoundError(error)) {
              return of(
                ProductListActions.loadProductsForSelectionSuccess({
                  products: emptyPagedListResponse<Product>(queryParams, 'Items::Product'),
                })
              );
            }

            return of(
              ProductListActions.loadProductsForSelectionFailure({
                error: {message: 'An error occurred, please refresh and try again.', code: MISSING_CATALOG_ERROR_CODE},
              })
            );
          })
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private productApi: ProductApiService
  ) {}
}
